import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { StepIcon } from './stepIcons';

// ──────────────────────────────────────────────────────────────────────────────
// ONBOARDING WALKTHROUGH (corner-panel mode)
// Wizardul stă într-un panel mic în colțul dreapta-jos. Pagina rămâne complet vizibilă.
// La „Următorul" → marchează seen + navighează la pagina noului step. Conținutul panelului
// face fade-out → fade-in pentru tranziție subtilă (nu palpâie).
// Dacă pagina la care navighez e blocată (`/unauthorized` sau `/login`), stepul e sărit silent
// la următorul — nu ținem userul prins într-un loop.
// Spotlight (când există un element `[data-onboarding="<stepId>"]`): doar un border pulsing pe
// element, FĂRĂ overlay — pagina rămâne interactivă.
// ──────────────────────────────────────────────────────────────────────────────

const SESSION_FLAG = 'walkthroughActive';
const setSessionFlag = (val) => {
  try {
    if (val) sessionStorage.setItem(SESSION_FLAG, 'true');
    else sessionStorage.removeItem(SESSION_FLAG);
  } catch (_) {
    /* private mode */
  }
};

const BLOCKED_PATHS = ['/unauthorized', '/login'];

// Compară pagini ignorând query string și hash. `navigateTo` poate avea `?tab=...` ca să
// deschidă un tab anume pe pagină — dar `location.pathname` nu include query, deci comparația
// directă ar declanșa fals minimize. Folosim doar partea de path.
const samePath = (a, b) => {
  if (!a || !b) return false;
  const stripA = a.split('?')[0].split('#')[0];
  const stripB = b.split('?')[0].split('#')[0];
  return stripA === stripB;
};

// Highlight border subtil pe elementul stepului — fără overlay, doar atrage privirea.
const SpotlightBorder = ({ selector }) => {
  const [rect, setRect] = useState(null);

  const recalc = useCallback(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(selector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect(r);
    const inView = r.top >= 0 && r.bottom <= window.innerHeight;
    if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selector]);

  useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [recalc]);

  if (!rect) return null;
  return (
    <div
      className="fixed pointer-events-none border-2 border-primary-500 rounded-md transition-all duration-300"
      style={{
        top: rect.top - 5,
        left: rect.left - 5,
        width: rect.width + 10,
        height: rect.height + 10,
        boxShadow: '0 0 0 4px rgba(59,130,246,0.18), 0 0 24px rgba(59,130,246,0.35)',
        zIndex: 55,
      }}
    />
  );
};

// Panel principal în colț dreapta-jos. Conținutul se schimbă cu fade subtil între stepuri.
const CornerPanel = ({
  step,
  stepIndex,
  totalSteps,
  t,
  onPrev,
  onNext,
  onSkip,
  onSkipAll,
  onFinish,
  isFirst,
  isLast,
  isRequired,
  minimized,
  onResume,
}) => {
  // Dublu state: `displayStep` afișat în UI, `step` e cel viitor. La schimbare stepului,
  // pornim fade-out, după 180 ms swap displayStep, apoi fade-in.
  const [displayStep, setDisplayStep] = useState(step);
  const [phase, setPhase] = useState('in'); // 'in' | 'out'

  useEffect(() => {
    if (!step) return;
    if (displayStep?.id === step.id) return;
    // Tranziție mai amplă pentru senzație blândă: 320ms fade-out, swap, 320ms fade-in.
    // Rezultatul: schimbarea conținutului panoului nu sare brusc — pare un dissolve subtil
    // pe fundalul paginii care se schimbă în paralel.
    setPhase('out');
    const id = setTimeout(() => {
      setDisplayStep(step);
      setPhase('in');
    }, 320);
    return () => clearTimeout(id);
  }, [step, displayStep]);

  if (!displayStep) return null;

  if (minimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[60] bg-white dark:bg-slate-800 rounded-xl border-2 border-primary-500 dark:border-primary-400 overflow-hidden"
        style={{
          animation: 'obSlideUp 260ms ease-out',
          boxShadow: '0 20px 40px -8px rgba(59,130,246,0.4)',
        }}
      >
        <button
          onClick={onResume}
          className="flex items-center gap-2 px-4 py-3 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition w-full"
        >
          <svg
            className="w-4 h-4 text-primary-600 dark:text-primary-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"
            />
          </svg>
          {t('onboarding.minimized.resumeBtn')}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSkipAll();
            }}
            className="ml-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-base leading-none"
            aria-label={t('onboarding.welcome.skipAllBtn')}
          >
            ×
          </button>
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] w-[360px] bg-white dark:bg-slate-800 rounded-xl border-2 border-primary-500 dark:border-primary-400 overflow-hidden"
      style={{
        animation: 'obSlideUp 280ms ease-out',
        boxShadow: '0 20px 50px -12px rgba(59,130,246,0.45), 0 8px 24px -8px rgba(0,0,0,0.25)',
      }}
    >
      {/* Bandă top primary — diferențiază clar wizardul de fundal */}
      <div className="h-1 bg-primary-500 dark:bg-primary-400" />
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-primary-50/50 dark:bg-primary-950/30 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-primary-700 dark:text-primary-200 truncate">
            {t('onboarding.welcome.title')}
          </p>
          <p className="text-[10px] text-slate-600 dark:text-slate-400">
            {t('onboarding.progress.stepCount', { current: stepIndex + 1, total: totalSteps })}
          </p>
        </div>
        <button
          onClick={onSkipAll}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-base leading-none flex-shrink-0"
          aria-label={t('onboarding.welcome.skipAllBtn')}
          title={t('onboarding.welcome.skipAllBtn')}
        >
          ×
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-100 dark:bg-slate-700">
        <div
          className="h-full bg-primary-600 transition-all duration-500"
          style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
        />
      </div>

      {/* Body — fade subtil la schimbarea stepului (320ms ease-in-out) */}
      <div
        className={`px-4 py-4 transition-all duration-300 ease-in-out ${phase === 'out' ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'}`}
      >
        <div className="flex items-start gap-3 mb-2">
          <div className="flex-shrink-0 mt-0.5">
            <StepIcon stepId={displayStep.id} size="sm" />
          </div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">
            {t(displayStep.titleKey)}
          </h4>
        </div>
        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
          {t(displayStep.descKey)}
        </p>
        {isRequired && (
          <p className="mt-2 text-[10px] text-amber-700 dark:text-amber-300 italic">
            {t('onboarding.controls.requiredHint')}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2">
        <div>
          {!isFirst && (
            <button
              onClick={onPrev}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              {t('onboarding.controls.prev')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isRequired && (
            <button
              onClick={onSkip}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline"
            >
              {t('onboarding.controls.skip')}
            </button>
          )}
          <Button variant="primary" size="sm" onClick={isLast ? onFinish : onNext}>
            {isLast ? t('onboarding.controls.finish') : t('onboarding.controls.next')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CompletionView = ({ t, onClose }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
    <div
      className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-8 text-center"
      style={{ animation: 'obFadeIn 300ms ease-out' }}
    >
      <div className="w-16 h-16 mx-auto mb-4 text-emerald-600 dark:text-emerald-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
        {t('onboarding.completion.title')}
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {t('onboarding.completion.subtitle')}
      </p>
      <Button variant="primary" onClick={onClose} className="mt-5">
        {t('onboarding.completion.closeBtn')}
      </Button>
    </div>
  </div>
);

const SkipConfirm = ({ t, onYes, onNo }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/70 p-4">
    <div
      className="w-full max-w-md bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6"
      style={{ animation: 'obFadeIn 200ms ease-out' }}
    >
      <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">
        {t('onboarding.skipConfirm.title')}
      </h3>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
        {t('onboarding.skipConfirm.body')}
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onNo}>
          {t('onboarding.skipConfirm.no')}
        </Button>
        <Button variant="danger" size="sm" onClick={onYes}>
          {t('onboarding.skipConfirm.yes')}
        </Button>
      </div>
    </div>
  </div>
);

// Keyframes inline pentru animații (nu modificăm tailwind.config.js, rămâne self-contained)
const Animations = () => (
  <style>{`
        @keyframes obFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes obSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    `}</style>
);

const OnboardingWizard = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mode, setMode] = useState('coach'); // 'coach' | 'minimized' | 'completion'
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [stabilized, setStabilized] = useState(false);
  const expectingNav = useRef(false);

  useEffect(() => {
    setSessionFlag(true);
    return () => setSessionFlag(false);
  }, []);

  // Perioadă de stabilizare la mount: ignorăm schimbările de pathname pentru primele ~1500ms.
  // Asta acoperă cazul Layout-ului care face redirecturi în lanț (signature/route modal-uri),
  // ca walkthrough-ul să nu intre în „minimize" prematur și să palpâie.
  useEffect(() => {
    const t = setTimeout(() => setStabilized(true), 1500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/onboarding/progress')
      .then((res) => {
        if (cancelled) return;
        const fresh = res.data.steps || [];
        const remaining = fresh.filter((s) => !s.seen);
        const list = remaining.length > 0 ? remaining : fresh;
        setSteps(list);
        if (list.length === 0 || remaining.length === 0) {
          setMode('completion');
        } else {
          // Întârziem prima navigare cu 600ms ca să se stabilizeze pagina inițială.
          // Important pentru cazul când userul tocmai a închis un modal (traseu/semnătură)
          // și DOM-ul abia s-a așezat — fără delay, navigarea programatică ar putea
          // intra într-o cursă cu re-renderul Layout-ului.
          const first = list[0];
          const currentFull = location.pathname + (location.search || '');
          if (first.navigateTo && first.navigateTo !== currentFull) {
            setTimeout(() => {
              if (cancelled) return;
              expectingNav.current = true;
              navigate(first.navigateTo);
            }, 600);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Loading error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = steps[currentIndex] || null;
  const isLast = currentIndex === steps.length - 1;
  const isFirst = currentIndex === 0;

  const markSeen = useCallback(async (stepId) => {
    try {
      await api.post('/onboarding/seen-step', { stepId });
    } catch (_) {
      /* best-effort */
    }
  }, []);

  const completeAndClose = useCallback(async () => {
    try {
      await api.post('/onboarding/complete');
    } catch (_) {
      /* best-effort */
    }
    setSessionFlag(false);
    try {
      window.dispatchEvent(new Event('onboarding:closed'));
    } catch (_) {}
    // Tour-ul navighează prin pagini (ex: ultimul pas e pe /manual). La finalizare aducem
    // userul înapoi pe Dashboard — nu-l lăsăm „abandonat" pe ultima pagină vizitată de tour.
    if (location.pathname !== '/dashboard') {
      expectingNav.current = true;
      navigate('/dashboard');
    }
    if (onClose) onClose();
  }, [onClose, navigate, location.pathname]);

  const navigateToStep = useCallback(
    (index) => {
      const step = steps[index];
      if (!step?.navigateTo) return;
      // Compar URL-ul COMPLET (pathname + search) — important pentru pași care doar schimbă
      // tab-ul (`?tab=...`). `samePath` ar returna true și NU am mai naviga, lăsând userul
      // pe tab-ul vechi (ex: ITM când stepul curent vrea Lista angajaților).
      const currentFull = location.pathname + (location.search || '');
      if (step.navigateTo !== currentFull) {
        expectingNav.current = true;
        navigate(step.navigateTo);
      }
    },
    [steps, navigate, location.pathname, location.search]
  );

  // Detectare nav. Patru cazuri:
  //  1) Stabilizare incompletă → ignor toate schimbările (mount-time chaos).
  //  2) Pagina e blocată (`/unauthorized` sau `/login`) → skip silent stepul, mergi la următorul.
  //  3) `expectingNav` setat → ignor (e nav. programatică pe care noi am declanșat-o).
  //  4) Pathname diferă de navigateTo-ul stepului curent → user a navigat manual → minimize.
  useEffect(() => {
    if (mode !== 'coach' || !current) return;
    if (!stabilized) return;

    if (BLOCKED_PATHS.includes(location.pathname)) {
      expectingNav.current = false;
      if (currentIndex < steps.length - 1) {
        const nextIdx = currentIndex + 1;
        setCurrentIndex(nextIdx);
        const nextStep = steps[nextIdx];
        if (nextStep?.navigateTo && !samePath(nextStep.navigateTo, location.pathname)) {
          expectingNav.current = true;
          navigate(nextStep.navigateTo);
        }
      } else {
        setMode('completion');
      }
      return;
    }

    if (expectingNav.current) {
      expectingNav.current = false;
      return;
    }

    if (current.navigateTo && !samePath(current.navigateTo, location.pathname)) {
      setMode('minimized');
    }
  }, [location.pathname, mode, current, currentIndex, steps, navigate, stabilized]);

  const handleNext = async () => {
    if (current) await markSeen(current.id);
    if (isLast) {
      setMode('completion');
    } else {
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      navigateToStep(nextIdx);
    }
  };

  const handleSkipStep = () => {
    if (current?.required) return;
    if (isLast) {
      setMode('completion');
    } else {
      const nextIdx = currentIndex + 1;
      setCurrentIndex(nextIdx);
      navigateToStep(nextIdx);
    }
  };

  const handlePrev = () => {
    if (isFirst) return;
    const prevIdx = currentIndex - 1;
    setCurrentIndex(prevIdx);
    navigateToStep(prevIdx);
  };

  const handleResume = () => {
    setMode('coach');
    if (!current?.navigateTo) return;
    const currentFull = location.pathname + (location.search || '');
    if (current.navigateTo !== currentFull) {
      expectingNav.current = true;
      navigate(current.navigateTo);
    }
  };

  const handleSkipAll = () => setShowSkipConfirm(true);
  const handleConfirmSkipAll = async () => {
    setShowSkipConfirm(false);
    await completeAndClose();
  };

  if (loading) {
    return (
      <div className="fixed bottom-4 right-4 z-[60] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-3">
        <Spinner label={t('common.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed bottom-4 right-4 z-[60] bg-white dark:bg-slate-800 rounded-lg shadow-2xl border border-danger-300 dark:border-danger-700 p-4 max-w-sm">
        <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
        <Button variant="ghost" size="sm" onClick={completeAndClose} className="mt-2">
          {t('common.close')}
        </Button>
      </div>
    );
  }

  if (mode === 'completion') {
    return (
      <>
        <CompletionView t={t} onClose={completeAndClose} />
        <Animations />
      </>
    );
  }

  if (!current) return null;

  return (
    <>
      {/* Spotlight border (fără overlay) — apare DOAR când există elementul în pagină */}
      {mode === 'coach' && current.selector && <SpotlightBorder selector={current.selector} />}

      <CornerPanel
        step={current}
        stepIndex={currentIndex}
        totalSteps={steps.length}
        t={t}
        onPrev={handlePrev}
        onNext={handleNext}
        onSkip={handleSkipStep}
        onSkipAll={handleSkipAll}
        onFinish={async () => {
          if (current) await markSeen(current.id);
          setMode('completion');
        }}
        isFirst={isFirst}
        isLast={isLast}
        isRequired={!!current.required}
        minimized={mode === 'minimized'}
        onResume={handleResume}
      />

      {showSkipConfirm && (
        <SkipConfirm t={t} onYes={handleConfirmSkipAll} onNo={() => setShowSkipConfirm(false)} />
      )}
      <Animations />
    </>
  );
};

export default OnboardingWizard;
